import React, { useState, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight, Calendar, Clock, Users, MapPin, AlertCircle, CheckCircle, XCircle, Loader, Filter, ChevronDown, ChevronUp } from "lucide-react";

// Transform fork booking data to standard booking format
const transformForkBooking = (forkBooking) => {
  const parseDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return { date: "", time: "" };
    try {
      const date = new Date(dateTimeStr);
      const dateStr = date.toISOString().split("T")[0];
      const timeStr = date.toTimeString().split(" ")[0].substring(0, 5);
      return { date: dateStr, time: timeStr };
    } catch {
      return { date: "", time: "" };
    }
  };

  const startDateTime = parseDateTime(forkBooking.bsTime);
  const endDateTime = parseDateTime(forkBooking.beTime);

  let status = forkBooking.status || "Pending";
  if (status === "Inactive") status = "Pending";
  if (!status || status === "") status = "Pending";

  return {
    bId: forkBooking.bId,
    vehicleId: forkBooking.vId,
    bookedDate: startDateTime.date,
    startTime: startDateTime.time,
    endTime: endDateTime.time,
    dSNo: forkBooking.dSNo,
    status: status,
    workScope: forkBooking.WorkScope || "",
    workArea: forkBooking.WorkArea || "",
    remark: forkBooking.Remark || "",
    from: forkBooking.From || "",
    to: forkBooking.TO || "",
    bookingStatus: forkBooking.BookingStatus || "",
  };
};

const isScheduledBooking = (booking) => {
  const bookingStatus = String(
    booking?.bookingStatus || booking?.BookingStatus || ""
  ).trim().toUpperCase();

  return (
    bookingStatus === "S" ||
    bookingStatus === "SHEDULE BOOKING" ||
    bookingStatus === "SCHEDULE BOOKING"
  );
};

// Week Day Card Component
function WeekDayCard({ date, dateStr, isToday, isSelected, bookingCount, onSelect }) {
  return (
    <button
      onClick={() => onSelect(dateStr)}
      className={`p-3 rounded-lg border transition-all hover:shadow-md ${
        isSelected
          ? "border-gray-900 bg-gray-50 shadow-md"
          : isToday
          ? "border-gray-300 bg-white hover:border-gray-400"
          : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      <div className="text-[10px] font-medium text-gray-500 mb-1">
        {date.toLocaleDateString("en-US", { weekday: "short" })}
      </div>
      <div className={`text-base font-semibold ${isSelected ? "text-gray-900" : isToday ? "text-gray-800" : "text-gray-700"} mb-1`}>
        {date.getDate()}
      </div>
      <div className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full inline-block">
        {bookingCount} bookings
      </div>
    </button>
  );
}

// Vehicle Selection Card Component
function VehicleSelectionCard({ groupName, vehicles, selectedVehicles, expanded, onToggleExpand, onSelectGroup, onToggleVehicle }) {
  const allSelected = vehicles.every(v => selectedVehicles.includes(v.vehicleId));
  
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between p-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-900">{groupName}</span>
          <span className="text-[10px] text-gray-500">({vehicles.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onSelectGroup(groupName, !allSelected)}
            className="text-[10px] text-blue-600 hover:text-blue-700 font-medium"
          >
            {allSelected ? "Deselect All" : "Select All"}
          </button>
          <button
            onClick={() => onToggleExpand(groupName)}
            className="p-1 hover:bg-gray-200 rounded transition"
          >
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        </div>
      </div>
      
      {expanded && (
        <div className="p-2 space-y-1">
          {vehicles.map(vehicle => (
            <label key={vehicle.vehicleId} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={selectedVehicles.includes(vehicle.vehicleId)}
                onChange={() => onToggleVehicle(vehicle.vehicleId)}
                className="w-3.5 h-3.5 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
              />
              <div className="flex-1">
                <span className="text-xs font-medium text-gray-700">{vehicle.vehicleId}</span>
                <span className="text-[10px] text-gray-500 ml-1">({vehicle.vehicleType})</span>
              </div>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// Booking Bar Component
function BookingBar({ booking, statusColors, onClick }) {
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(":").map(Number);
    return (hours - 6) * 60 + minutes;
  };

  const startMin = timeToMinutes(booking.startTime);
  const endMin = timeToMinutes(booking.endTime);
  const totalMinutes = 14 * 60;
  const left = (startMin / totalMinutes) * 100;
  const width = ((endMin - startMin) / totalMinutes) * 100;

  const statusColor = statusColors[booking.status] || "#6c757d";
  const isCancelled = booking.status === "Cancelled";

  return (
    <div
      className="absolute top-1 bottom-1 rounded cursor-pointer hover:opacity-80 transition flex items-center px-1.5"
      style={{
        left: `${Math.max(0, left)}%`,
        width: `${Math.min(100 - left, width)}%`,
        backgroundColor: statusColor,
        backgroundImage: isCancelled ? "repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255,255,255,0.2) 5px, rgba(255,255,255,0.2) 10px)" : "none",
      }}
      onClick={() => onClick(booking)}
      title={`${booking.startTime} - ${booking.endTime}\nVehicle: ${booking.vehicleId}\nDriver: ${booking.dSNo || "N/A"}\nFrom: ${booking.from || "N/A"} → To: ${booking.to || "N/A"}`}
    >
      <span className="text-[9px] text-white font-medium truncate drop-shadow">
        {booking.startTime}-{booking.endTime}
      </span>
    </div>
  );
}

const BookingGraph = ({ bookings = [], vehicles = [], onCreateBooking, onEditBooking, initialDate = null, onDateChange }) => {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(initialDate || new Date().toISOString().split("T")[0]);
  const [showVehicleSelection, setShowVehicleSelection] = useState(false);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  
  const transformedBookings = useMemo(() => {
    return bookings.map(b => {
      if (b.bsTime || b.beTime) {
        return transformForkBooking(b);
      }
      return b;
    });
  }, [bookings]);

  const vehicleGroups = useMemo(() => {
    const groups = {};
    vehicles.forEach(vehicle => {
      const type = vehicle.vehicleType || "Other";
      if (!groups[type]) groups[type] = [];
      groups[type].push(vehicle);
    });
    return groups;
  }, [vehicles]);

  const weekDates = useMemo(() => {
    const dates = [];
    const startOfWeek = new Date(currentWeek);
    startOfWeek.setDate(currentWeek.getDate() - currentWeek.getDay() + 1);

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [currentWeek]);

  const dayBookings = useMemo(() => {
    if (selectedVehicles.length === 0) {
      return transformedBookings.filter(b =>
        b.bookedDate === selectedDate &&
        isScheduledBooking(b)
      );
    }
    return transformedBookings.filter(b =>
      b.bookedDate === selectedDate &&
      isScheduledBooking(b) &&
      selectedVehicles.includes(b.vehicleId)
    );
  }, [transformedBookings, selectedDate, selectedVehicles]);

  const vehiclesWithBookings = useMemo(() => {
    return [...new Set(dayBookings.map(b => b.vehicleId))].sort();
  }, [dayBookings]);

  const statusColors = {
    Confirmed: "#10b981",
    Pending: "#f59e0b",
    Conflict: "#ef4444",
    Completed: "#6b7280",
    Cancelled: "#9ca3af"
  };

  const navigateWeek = (direction) => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(currentWeek.getDate() + (direction * 7));
    setCurrentWeek(newWeek);
  };

  const goToToday = () => {
    setCurrentWeek(new Date());
    const today = new Date().toISOString().split("T")[0];
    setSelectedDate(today);
    if (onDateChange) onDateChange(today);
  };

  const toggleVehicle = (vehicleId) => {
    setSelectedVehicles(prev =>
      prev.includes(vehicleId)
        ? prev.filter(id => id !== vehicleId)
        : [...prev, vehicleId]
    );
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const selectGroup = (groupName, selectAll = true) => {
    const groupVehicles = vehicleGroups[groupName].map(v => v.vehicleId);
    if (selectAll) {
      setSelectedVehicles(prev => [...new Set([...prev, ...groupVehicles])]);
    } else {
      setSelectedVehicles(prev => prev.filter(id => !groupVehicles.includes(id)));
    }
  };

  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(":").map(Number);
    return (hours - 6) * 60 + minutes;
  };

  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60) + 6;
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
  };

  const handleEmptySlotClick = (vehicleId, startTime, endTime) => {
    if (onCreateBooking) {
      onCreateBooking({ vehicleId, bookedDate: selectedDate, startTime, endTime });
    }
  };

  const handleBookingClick = (booking) => {
    if (onEditBooking) {
      onEditBooking(booking);
    }
  };

  const handleDateSelect = (dateStr) => {
    setSelectedDate(dateStr);
    if (onDateChange) onDateChange(dateStr);
  };

  return (
    <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 bg-gray-50">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-gray-900 rounded-lg">
              <Calendar size={14} className="text-white" />
            </div>
            <h2 className="text-sm font-semibold text-gray-900">Booking Calendar</h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-[10px]">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <span className="text-gray-600">Confirmed</span>
            </div>
            <div className="flex items-center gap-1 text-[10px]">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              <span className="text-gray-600">Pending</span>
            </div>
            <div className="flex items-center gap-1 text-[10px]">
              <span className="w-2 h-2 rounded-full bg-gray-500"></span>
              <span className="text-gray-600">Completed</span>
            </div>
            <div className="flex items-center gap-1 text-[10px]">
              <span className="w-2 h-2 rounded-full bg-gray-300"></span>
              <span className="text-gray-600">Cancelled</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Week Navigation */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigateWeek(-1)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition"
            >
              <ChevronLeft size={14} className="text-gray-500" />
            </button>
            <button
              onClick={goToToday}
              className="px-2 py-1 text-[10px] font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
            >
              Today
            </button>
            <button
              onClick={() => navigateWeek(1)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition"
            >
              <ChevronRight size={14} className="text-gray-500" />
            </button>
          </div>
          
          <button
            onClick={() => setShowVehicleSelection(!showVehicleSelection)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-medium rounded-lg transition ${
              showVehicleSelection
                ? "bg-gray-100 text-gray-700"
                : "bg-gray-900 text-white hover:bg-gray-800"
            }`}
          >
            <Filter size={10} />
            {showVehicleSelection ? "Hide Filters" : "Filter Vehicles"}
          </button>
        </div>

        {/* Week Days Grid */}
        <div className="grid grid-cols-7 gap-1.5">
          {weekDates.map((date, index) => {
            const dateStr = date.toISOString().split("T")[0];
            const isToday = dateStr === new Date().toISOString().split("T")[0];
            const isSelected = dateStr === selectedDate;
            const weekDayBookings = transformedBookings.filter(
              (b) => b.bookedDate === dateStr && isScheduledBooking(b)
            );

            return (
              <WeekDayCard
                key={index}
                date={date}
                dateStr={dateStr}
                isToday={isToday}
                isSelected={isSelected}
                bookingCount={weekDayBookings.length}
                onSelect={handleDateSelect}
              />
            );
          })}
        </div>

        {/* Vehicle Selection Panel */}
        {showVehicleSelection && (
          <div className="border border-gray-200 rounded-lg p-3">
            <h3 className="text-xs font-semibold text-gray-900 mb-2">Filter by Vehicle</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {Object.entries(vehicleGroups).map(([groupName, groupVehicles]) => (
                <VehicleSelectionCard
                  key={groupName}
                  groupName={groupName}
                  vehicles={groupVehicles}
                  selectedVehicles={selectedVehicles}
                  expanded={expandedGroups[groupName]}
                  onToggleExpand={toggleGroup}
                  onSelectGroup={selectGroup}
                  onToggleVehicle={toggleVehicle}
                />
              ))}
            </div>
          </div>
        )}

        {/* Gantt Chart */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {/* Date Header */}
          <div className="p-3 bg-gray-50 border-b border-gray-200">
            <p className="text-xs font-medium text-gray-900">
              {new Date(selectedDate).toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric"
              })}
            </p>
          </div>

          {vehiclesWithBookings.length > 0 ? (
            <div className="overflow-x-auto">
              {/* Time Header */}
              <div className="bg-gray-100 border-b border-gray-200 min-w-[800px]">
                <div className="flex items-center p-2">
                  <div className="w-28 flex-shrink-0">
                    <div className="flex items-center gap-1 text-[10px] text-gray-500">
                      <Clock size={10} />
                      <span>06:00 - 20:00</span>
                    </div>
                  </div>
                  <div className="flex-1 flex">
                    {Array.from({ length: 14 }, (_, i) => (
                      <div key={i} className="flex-1 text-center text-[9px] text-gray-500 py-1">
                        {minutesToTime(i * 60)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Vehicle Rows */}
              <div className="divide-y divide-gray-100 min-w-[800px]">
                {vehiclesWithBookings.map(vehicleId => {
                  const vehicleBookings = dayBookings.filter(b => b.vehicleId === vehicleId);
                  const vehicle = vehicles.find(v => v.vehicleId === vehicleId);

                  return (
                    <div key={vehicleId} className="flex items-center p-2 hover:bg-gray-50 transition">
                      {/* Vehicle Label */}
                      <div className="w-28 flex-shrink-0 pr-2">
                        <p className="text-xs font-semibold text-gray-900 truncate">{vehicleId}</p>
                        <p className="text-[10px] text-gray-500 truncate">{vehicle?.vehicleType || ""}</p>
                      </div>

                      {/* Timeline */}
                      <div className="flex-1 relative h-10 bg-gray-50 rounded border border-gray-200">
                        {/* Time grid lines */}
                        {Array.from({ length: 14 }, (_, i) => (
                          <div
                            key={i}
                            className="absolute top-0 h-full border-r border-gray-200"
                            style={{ left: `${((i + 1) / 14) * 100}%` }}
                          />
                        ))}

                        {/* Booking bars */}
                        {vehicleBookings.map((booking, index) => (
                          <BookingBar
                            key={`${booking.bId}-${index}`}
                            booking={booking}
                            statusColors={statusColors}
                            onClick={handleBookingClick}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="flex flex-col items-center gap-2">
                <Calendar size={24} className="text-gray-300" />
                <p className="text-sm font-medium text-gray-900">No Bookings for This Day</p>
                <p className="text-xs text-gray-500">Select a different date from the week view</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingGraph;